import { useState, useEffect, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { getCountryConfig } from '../lib/country-config'

export default function SelfieMobile() {
  const { t, i18n } = useTranslation()
  const { mobileToken } = useParams<{ mobileToken: string }>()
  const [status, setStatus] = useState<'loading' | 'ready' | 'captured' | 'uploading' | 'done' | 'error'>('loading')
  const [signerInfo, setSignerInfo] = useState<{ signerName: string; documentName: string; countryCode?: string } | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [error, setError] = useState('')
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)

  useEffect(() => {
    fetch(`/api/sign/selfie-mobile/${mobileToken}`)
      .then(r => r.json())
      .then(data => {
        if (data.alreadyCaptured) { setStatus('done'); return }
        if (data.error) { setError(data.error); setStatus('error'); return }
        const cc = getCountryConfig(data.countryCode)
        if (cc) {
          i18n.changeLanguage(cc.locale)
          document.documentElement.dir = cc.rtl ? 'rtl' : 'ltr'
          document.documentElement.lang = cc.locale
        }
        setSignerInfo(data)
        setStatus('ready')
      })
      .catch(() => { setError(t('common.error', 'An error occurred')); setStatus('error') })
  }, [mobileToken])

  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } }
      })
      streamRef.current = stream
      if (videoRef.current) videoRef.current.srcObject = stream
    } catch {
      setError(t('sign.selfie.cameraError', 'Could not access the camera'))
      setStatus('error')
    }
  }, [])

  useEffect(() => {
    if (status === 'ready') startCamera()
    return () => { streamRef.current?.getTracks().forEach(t => t.stop()) }
  }, [status, startCamera])

  function capture() {
    const video = videoRef.current
    if (!video) return
    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    canvas.getContext('2d')?.drawImage(video, 0, 0)
    const dataUrl = canvas.toDataURL('image/jpeg', 0.85)
    setPreview(dataUrl)
    streamRef.current?.getTracks().forEach(t => t.stop())
    setStatus('captured')
  }

  function retake() {
    setPreview(null)
    setStatus('ready')
  }

  async function upload() {
    if (!preview) return
    setStatus('uploading')
    try {
      const blob = await (await fetch(preview)).blob()
      const form = new FormData()
      form.append('file', blob, 'selfie.jpg')
      const res = await fetch(`/api/sign/selfie-mobile/${mobileToken}/upload`, {
        method: 'POST', body: form,
      })
      const data = await res.json()
      if (data.success) {
        setStatus('done')
      } else {
        setError(data.error || t('sign.selfie.uploadFailed', 'Upload failed'))
        setStatus('error')
      }
    } catch {
      setError(t('sign.selfie.uploadFailed', 'Upload failed'))
      setStatus('error')
    }
  }

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white" />
      </div>
    )
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl p-8 max-w-sm w-full text-center">
          <div className="text-4xl mb-4">&#x26A0;&#xFE0F;</div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">{t('common.error', 'Error')}</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    )
  }

  if (status === 'done') {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl p-8 max-w-sm w-full text-center">
          <div className="text-5xl mb-4">&#x2705;</div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">{t('sign.selfie.mobileDone', 'Selfie captured! You can close this page.')}</h1>
          <p className="text-gray-600">{t('sign.selfie.mobileReturn', 'You can close this page and return to your computer to continue signing.')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col">
      {/* Header */}
      <div className="bg-gray-800 px-4 py-3 text-center">
        <div className="text-indigo-400 font-bold text-lg">PAKT</div>
        {signerInfo && (
          <p className="text-gray-400 text-xs mt-1">
            {t('sign.selfie.mobileTitle', 'Take a Selfie')} &mdash; {signerInfo.documentName}
          </p>
        )}
      </div>

      {/* Camera / Preview */}
      <div className="flex-1 flex items-center justify-center p-4">
        {status === 'ready' && (
          <div className="relative w-full max-w-sm aspect-[3/4] bg-black rounded-2xl overflow-hidden">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover scale-x-[-1]"
            />
            <div className="absolute inset-0 border-4 border-white/20 rounded-2xl pointer-events-none" />
            <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/60 to-transparent">
              <p className="text-white text-center text-sm">{t('sign.selfie.mobilePosition', 'Position your face in the frame')}</p>
            </div>
          </div>
        )}

        {status === 'captured' && preview && (
          <div className="w-full max-w-sm">
            <img src={preview} alt={t('sign.selfie.previewAlt', 'Selfie preview')} className="w-full rounded-2xl scale-x-[-1]" />
          </div>
        )}

        {status === 'uploading' && (
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white mx-auto mb-4" />
            <p className="text-white">{t('sign.selfie.uploading', 'Uploading...')}</p>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="p-4 pb-8 space-y-3">
        {status === 'ready' && (
          <button onClick={capture}
            className="w-full py-4 bg-white text-gray-900 font-bold rounded-xl text-lg active:bg-gray-200 transition">
            {t('sign.selfie.capture', 'Take Photo')}
          </button>
        )}

        {status === 'captured' && (
          <>
            <button onClick={upload}
              className="w-full py-4 bg-indigo-600 text-white font-bold rounded-xl text-lg active:bg-indigo-700 transition">
              {t('sign.selfie.mobileConfirm', 'Confirm & Send')}
            </button>
            <button onClick={retake}
              className="w-full py-3 bg-gray-700 text-gray-300 font-medium rounded-xl active:bg-gray-600 transition">
              {t('sign.selfie.mobileRetake', 'Retake')}
            </button>
          </>
        )}
      </div>
    </div>
  )
}
